import { useEffect, useState } from "react";
import "./login.css";
import { version } from "../version";
import logoImage from "../aditum-logo.jpeg";
import { apiRoutes } from "../network/apiRoutes";
import { NetworkFetch } from "../network/networkFetch";
import { useUser } from "../context/UserContext";
import { useNavigate } from "react-router-dom";


export function App() {
  const [identifier, setIdentifier] = useState("");
  const [serverStatus, setServerStatus] = useState<"checking" | "online" | "offline">("checking");
  const [serverMessage, setServerMessage] = useState<string>("");
  const [loginError, setLoginError] = useState<string>("");
  const [loginSuccess, setLoginSuccess] = useState<boolean>(false);
  const { setUser } = useUser();
  const navigate = useNavigate();

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError("");
    setLoginSuccess(false);
    if (!identifier.trim()) {
      setLoginError("Por favor ingresa tu documento o celular.");
      return;
    }
    try {
      // Enviar el identificador al endpoint de login
      const response = await NetworkFetch(apiRoutes.login, {
        user: identifier.trim()
      });
      // Validar la respuesta del login

      if (response) {

        // Guardar user en contexto (UserProvider persistirá en localStorage)
        try {
          setUser({ ...response, identifier: identifier.trim() });
        } catch {
          // ignore
        }

        setLoginSuccess(true);
        setLoginError("");
        // Redirigir al dashboard
        try {
          navigate("/dashboard");
        } catch {
          // ignore
        }
      } else {
        setLoginError("No se pudo realizar la verificación. Verifica tus datos e intenta de nuevo.");
        setLoginSuccess(false);
      }
    } catch (error: any) {
      setLoginError("No se pudo realizar la verificación. Intenta más tarde.");
      setLoginSuccess(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    let intervalId: NodeJS.Timeout | null = null;

    const fetchServerStatus = async () => {
      setServerStatus("checking");
      try {
        await NetworkFetch(apiRoutes.root);
        if (isMounted) {
          setServerStatus("online");
          setServerMessage("Estamos listos para ayudarte");
        }
      } catch (error: any) {
        if (isMounted) {
          setServerStatus("offline");
          setServerMessage("El servicio no está disponible. Intenta más tarde.");
        }
      }
    };

    // Initial check on mount
    fetchServerStatus();

    // Poll every 10 seconds
    intervalId = setInterval(fetchServerStatus, 10000);

    return () => {
      isMounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  const statusIndicator = {
    checking: {
      color: "text-yellow-500",
      bg: "bg-yellow-50 border-yellow-200",
      label: "Verificando servicio...",
      message: "Comprobando el estado del servidor"
    },
    online: {
      color: "text-emerald-600",
      bg: "bg-emerald-50 border-emerald-200",
      label: "Servicio Disponible",
      message: serverMessage || "Estamos listos para ayudarte"
    },
    offline: {
      color: "text-red-500",
      bg: "bg-red-50 border-red-200",
      label: "Servicio No Disponible",
      message: serverMessage || "El servicio no está disponible. Intenta más tarde."
    }
  }[serverStatus];

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 relative overflow-hidden">
      {/* Elegant Background Pattern */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        aria-hidden="true"
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1200 800"
        preserveAspectRatio="xMidYMid slice"
      >
        {/* Large blur circles - top left corner */}
        <circle cx="0" cy="0" r="400" fill="#6366f1" opacity="0.06" filter="blur(40px)" />
        <circle cx="100" cy="100" r="250" fill="#818cf8" opacity="0.05" filter="blur(30px)" />
        
        {/* Large blur circles - bottom right corner */}
        <circle cx="1200" cy="800" r="450" fill="#4f46e5" opacity="0.06" filter="blur(40px)" />
        <circle cx="1100" cy="700" r="300" fill="#818cf8" opacity="0.04" filter="blur(35px)" />
        
        {/* Accent circles - middle areas */}
        <circle cx="1000" cy="200" r="180" fill="#c7d2fe" opacity="0.04" filter="blur(25px)" />
        <circle cx="200" cy="600" r="200" fill="#e0e7ff" opacity="0.05" filter="blur(28px)" />
        
        {/* Subtle wave pattern */}
        <defs>
          <filter id="softGlow">
            <feGaussianBlur stdDeviation="3" />
          </filter>
        </defs>
        
        {/* Curved accent lines for elegance */}
        <path
          d="M0,200 Q300,150 600,200 T1200,200"
          stroke="#6366f1"
          strokeWidth="1.5"
          fill="none"
          opacity="0.08"
          filter="url(#softGlow)"
        />
        <path
          d="M0,600 Q300,550 600,600 T1200,600"
          stroke="#818cf8"
          strokeWidth="1"
          fill="none"
          opacity="0.06"
          filter="url(#softGlow)"
        />
      </svg>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md mx-auto bg-white rounded-2xl shadow-xl px-6 sm:px-9 py-10 sm:py-12 flex flex-col items-center border border-gray-100">
        {/* Back Button */}
        <button
          onClick={() => navigate("/")}
          className="absolute top-6 left-6 p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 text-gray-600 hover:text-gray-900"
          aria-label="Volver al menú principal"
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
        </button>

        {/* Logo */}
        <div className="mb-8 w-full flex items-center justify-center">
          <img
            src={logoImage}
            alt="Aditum Logo"
            className="w-32 sm:w-40 h-auto object-contain"
          />
        </div>

        {/* Form Header */}
        <div className="mb-10 text-center w-full">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">Bienvenido</h2>
          <p className="text-base sm:text-lg text-gray-800 font-semibold mb-3">a su portal de autogestión</p>
          <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm font-medium mb-4">Aquí podrás:</p>
          
          {/* Features List */}
          <div className="space-y-2.5 mb-6">
            <div className="flex items-center justify-center gap-2.5">
              <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-gray-700 text-sm font-medium">Consultar el estado de tu cuenta</p>
            </div>
            <div className="flex items-center justify-center gap-2.5">
              <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-gray-700 text-sm font-medium">Realizar pagos</p>
            </div>
            <div className="flex items-center justify-center gap-2.5">
              <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-gray-700 text-sm font-medium">Gestionar tus servicios</p>
            </div>
          </div>
          
          <p className="text-gray-500 text-xs">Acceso seguro a tu portal</p>
        </div>

        <form onSubmit={handleContinue} className="w-full space-y-6">
          {/* Identifier Field - Highlighted Card */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-200 shadow-sm">
            <div className="space-y-2.5">
              <div className="flex items-center gap-2.5 mb-2">
                <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                </div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700">
                  Digite el documento o celular del titular del servicio:
                </label>
              </div>
              <div className="relative">
                <input
                  type="text"
                  value={identifier}
                  onChange={(e) => {
                    // Solo permite números
                    const onlyNumbers = e.target.value.replace(/[^0-9]/g, '');
                    setIdentifier(onlyNumbers);
                  }}
                  placeholder="Tu documento o celular"
                  required
                  className="w-full h-11 px-4 py-3 rounded-xl bg-white border-2 border-blue-300 text-gray-900 text-base placeholder-gray-450 transition-all duration-300 focus:outline-none focus:border-blue-600 focus:bg-white focus:ring-4 focus:ring-blue-200/60 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={serverStatus !== "online"}
                />
              </div>
              <p className="text-xs text-gray-600 font-medium">Sin símbolos ni caracteres especiales</p>
            </div>
          </div>

          {/* Continue Button */}
          <button
            type="submit"
            disabled={!identifier.trim() || serverStatus !== "online"}
            className={`w-full h-11 mt-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white text-base font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-blue-200/50`}
          >
            Continuar
          </button>
          {/* Login Error Message */}
          {loginError && (
            <div className="w-full bg-red-50 border border-red-300 text-red-700 rounded-xl px-4 py-3 text-sm text-center font-medium">
              {loginError}
            </div>
          )}
          {/* Login Success Message */}
          {loginSuccess && (
            <div className="w-full bg-emerald-50 border border-emerald-300 text-emerald-700 rounded-xl px-4 py-3 text-sm text-center font-medium">
              ¡Verificación exitosa!
            </div>
          )}
        </form>

        {/* Divider */}
        <div className="my-8 flex items-center gap-4 w-full">
          <div className="flex-1 h-px bg-gray-200"></div>
          <span className="text-gray-400 text-xs font-medium">Estado del servicio</span>
          <div className="flex-1 h-px bg-gray-200"></div>
        </div>

        {/* Server Status - Status Indicator Pattern */}
        <div className={`w-full p-4 border rounded-xl flex items-start gap-3 ${statusIndicator.bg}`}>
          <span className={`${statusIndicator.color} text-xl mt-1 flex-shrink-0`}>●</span>
          <div>
            <p className="font-semibold text-gray-900 text-xs tracking-wide">{statusIndicator.label}</p>
            <p className="text-xs text-gray-600 mt-1">{statusIndicator.message}</p>
          </div>
        </div>

        {/* Version Info */}
        <p className="text-center text-gray-400 text-xs mt-8 tracking-wide">versión {version}</p>
      </div>
    </div>
  );
}

export default App;
